import store from 'a/store';
import { getLocalization } from 'root/activities/selectors/languageSelectors';
import { logRumError } from 'lib/DDRum';

import { throwCustomAquaError } from '../activities/components/GlobalErrorHandler/actions';
import { ERROR_STRINGS } from '../activities/components/GlobalErrorHandler/constants';
import { fetchAudio, fetchJson, fetchAnimation } from './AssetLoaderFetch';
import { noop } from './utilityFunctions';
import { loadAsset, getLoadedComponent } from './assetLoading';
import InlineSvgStr from '../activities/components/InlineSvgStr';
import { LOCALIZATIONS } from './constants';

const lib = {
  throwGlobalError,
  ASSET_RETRY_TIME: 3000,
};

export { lib as _lib };

// a unique id that is assigned to each call of preLoadAssets. it will increment each time it is called.
let assetBundleId = 0;

// starts at false. if an error happens anywhere while loading this is flipped to true so the recursive and async
// parts of the asset loader can check this value and self quit if it is true. If even 1 error happens in the asset
// loader the aqua error screen will show so no reason to continue any other operations.
let hasError = false;

/**
 * Generic AssetLoader class built to be extended for a specific game.
 * Contains functions to handle the generic operations of parsing arrays of assets and loading them in order
 * while reporting back progress and triggering on complete calls.
 */
export default class AssetLoader {
  constructor() {
    this.assetCache = {};
    this.captions = {};
    this.loaders = [];
    // store is not available at the time AssetLoader is initialized that's why we access store in methods
    // currently loaded assets for progress tracking. uses the AssetMap batch object as a key to store the
    // progress for that batch of loads.
    this.progressCounter = {};
    this.showDebugLogs = false;
    // Toggle on for AssetLoader to keep track of all assets loaded and how many times they were played.
    // Useful for checking to see if you are loading assets that you never use.
    // Should always be false when merging into Integration.
    this.auditAssets = false;
    if (this.auditAssets) {
      this.auditStorage = {};
    }
  }

  getAssetFromCache = (assetName) => {
    // had to use variable instead of class method because of Object.freeze
    const countryCode = getLocalization(store.getState());
    const cacheKey = `${assetName}_${countryCode}`;
    if (this.assetCache[cacheKey]) {
      if (this.auditAssets) {
        this.auditStorage[cacheKey] += 1;
      }
      return this.assetCache[cacheKey];
    }
    if (this.assetCache[`${assetName}_en`]) {
      if (this.auditAssets) {
        this.auditStorage[`${assetName}_en`] += 1;
      }
      return this.assetCache[`${assetName}_en`];
    }
    logRumError(`Couldn't find asset in cache: ${cacheKey}`);
    return null;
  };

  /**
   * Pull an asset file from the cached asset store,
   * You must run preLoadAssets before retrieving audio files from this function
   * @param asset name of asset to load or valid asset object
   * @returns {*} The Asset! could be an svg, mp3, etc
   */
  getLoadedAsset = (assetObj) => {
    const filename = assetObj && assetObj.file ? assetObj.file : assetObj;

    if (assetObj?.file && this.getFileExtension(assetObj.file) === 'svg') {
      return getLoadedComponent(assetObj, InlineSvgStr);
    }

    if (!filename) {
      logRumError('Cannot load the asset');
      return null;
    }

    const asset = this.getAssetFromCache(filename);

    // PNGs and MP3s loaded through dynamic import changed in Webpack 4/Babel 7 so that
    // the asset is in the .default property of an object, just like JS files. But just
    // to make things interesting, files fetched from S3 are not wrapped in an object.
    // Accounting for all that here by returning the .default property of those file types
    // when they're wrapped in an object. Phew.
    const fileExt = this.getFileExtension(filename);

    return (fileExt === 'png' || fileExt === 'mp3') && asset && asset.default
      ? asset.default
      : asset;
  };

  // As above, but just returns the right caption for subtitles if one exists
  getCaption = (assetInfo) => {
    const filename = assetInfo && assetInfo.file ? assetInfo.file : assetInfo;
    return this.captions[filename] || '';
  };

  /**
   * returns the progress of the asset bundle with the passed in asset map key
   * @param id unique id for this asset load
   * @param AssetMap Object containing assets to load.
   * @returns {number} percentage complete from 0 - 1
   */
  getLoadedProgress = (id, AssetMap) => {
    this.logDevMessage(
      `${id}: ${this.progressCounter[id]} / ${this.getNumberOfItems(AssetMap)}`,
    );
    return this.progressCounter[id] / this.getNumberOfItems(AssetMap);
  };

  /**
   * clears the assetCache variable. doesn't actually unload from memory.
   * May not be necessary but can't hurt to cleanup the local asset store when a game unmounts.
   */
  clearAssetCache = () => {
    Object.keys(this.assetCache).forEach((categoryKey) => {
      delete this.assetCache[categoryKey];
    });
    Object.keys(this.captions).forEach((categoryKey) => {
      delete this.captions[categoryKey];
    });
  };

  /**
   * Store an asset in the asset cache
   * @param filename name of file to save. used as a key in the map
   * @param asset The Asset! could be an svg, mp3, etc
   */
  storeInCache = (filename, countryCode, asset, captions) => {
    const cacheKey = `${filename}_${countryCode}`;
    this.assetCache[cacheKey] = asset;
    this.logDevMessage(`storing ${cacheKey} in cache with data...`);
    if (captions !== undefined && Object.keys(captions).length !== 0) {
      const captionsByCode = {
        [LOCALIZATIONS.EN]: captions[LOCALIZATIONS.EN],
        [LOCALIZATIONS.ES]: captions[LOCALIZATIONS.ES] || captions[LOCALIZATIONS.EN],
        [LOCALIZATIONS.SPA_ES_DEFAULT]: captions[LOCALIZATIONS.SPA_ES_DEFAULT] || captions[LOCALIZATIONS.ES],
      };
      const currentCaption = captionsByCode[countryCode];
      this.captions[filename] = currentCaption;
    }
    if (this.auditAssets) {
      this.auditStorage[cacheKey] = 0;
    }
  };

  /**
   * uses the assets loaded and total assets trying to load to updated the current progress
   * @param id unique id for this asset load
   * @param AssetMap Object containing assets to load.
   * @param ProgressCallback function to call after each item in the map as been loaded to report back progress
   * @returns {number} percentage complete from 0 - 1
   */
  updateProgress = (
    id,
    AssetMap,
    ProgressCallback,
    CompleteCallback,
    reportingDetails,
  ) => {
    this.progressCounter[id]++;
    const loadedProgress = this.getLoadedProgress(id, AssetMap);
    if (ProgressCallback) {
      ProgressCallback(loadedProgress);
    }
    this.callbackIfComplete(loadedProgress, CompleteCallback, reportingDetails);
    return loadedProgress;
  };

  /**
   * Function to call from your game to load a bundle of assets
   * @param AssetMap Object containing assets to load. expecting this format:
   * Array or or map of objects
   [
   category1:{
        asset1: 'asset1',
        asset2: 'asset2',
        asset3: 'asset3',
      },
   category2:{
        asset1: 'asset1',
        asset2: 'asset2',
        asset3: 'asset3',
      },
   ];
   Look at showOff > assets > svgComponents > SvgMap.js for example
   Look at showOff > assets > audio > AudioMap.js for example
   * @param CompleteCallback function to call when all the assets in passed map have been loaded
   * @param ProgressCallback function to call after each item in the map as been loaded to report back progress
   */
  preLoadAssets = (AssetMap, CompleteCallback, ProgressCallback = noop) => {
    // throw warning if game did not set their gameId in their asset loader for reporting
    if (!this.gameId) {
      console.warn(
        'You need to set this.gameId = GAME_ID in your AssetLoader constructor',
      );
    }

    // had to use variable instead of class method because of Object.freeze
    const countryCode = getLocalization(store.getState());
    const id = assetBundleId;
    assetBundleId += 1;
    this.logDevMessage(
      `start loading an asset map ${this.getNumberOfItems(AssetMap)}`,
    );
    this.progressCounter[id] = 0;

    const startTime = performance.now();
    const reportingDetails = {
      details: [],
      startTime,
    };

    Object.keys(AssetMap).forEach((categoryKey) => {
      Object.keys(AssetMap[categoryKey]).forEach((key) => {
        const filename = AssetMap[categoryKey][key];
        this.loadAsset(
          id,
          AssetMap,
          filename,
          countryCode,
          CompleteCallback,
          ProgressCallback,
          reportingDetails,
        );
      });
    });
    return id;
  };

  preLoadLocalAndAws = (
    localAssets = [],
    awsAssets = [],
    completeCallback = data => data,
  ) => {
    const promises = [];
    if (localAssets.length > 0) {
      promises.push(
        new Promise((resolve) => {
          this.preLoadAssets(localAssets, resolve);
        }),
      );
    }
    if (awsAssets.length > 0) {
      promises.push(
        new Promise((resolve) => {
          this.preLoadAwsAssets(awsAssets, resolve);
        }),
      );
    }
    return Promise.all(promises).then(completeCallback);
  };

  getFileExtension = filename => filename.split('.').pop().toLowerCase();

  getFileFetcher = ({ file, hasAnimationFrames }) => {
    const extension = this.getFileExtension(file);

    let fetcher;

    if (hasAnimationFrames) {
      return fetchAnimation;
    }

    switch (extension) {
      case 'json':
        fetcher = fetchJson;
        break;
      case 'mp3':
        fetcher = fetchAudio;
        break;
      case 'svg':
        fetcher = loadAsset;
        break;
      default:
        break;
    }

    return fetcher;
  };

  /**
   * Loads a bundle of assets from AWS S3 bucket.
   * @param AssetMap Object containing assets to load. expecting this format:
   * Array or or map of objects
   [
   category1:{
        asset1: {name: "name", file: "file.mp3", path: "path/to/file/directory/"},
        asset2: {name: "name", file: "file.svg", path: "path/to/file/directory/"},
        asset3: {name: "name", file: "file.mp3", path: "path/to/file/directory/"},
      },
   category2:{
        asset1: {name: "name", file: "file.svg", path: "path/to/file/directory/"},
        asset2: {name: "name", file: "file.mp3", path: "path/to/file/directory/"},
        asset3: {name: "name", file: "file.svg", path: "path/to/file/directory/"},
      },
   ];
   * @param CompleteCallback function to call when all assets have been loaded
   * @param ProgressCallback function to call when each asset finishes loading
   */
  preLoadAwsAssets = (AssetMap, CompleteCallback, ProgressCallback = noop) => {
    // throw warning if game did not set their gameId in their asset loader for reporting
    if (!this.gameId) {
      console.warn(
        'You need to set this.gameId = GAME_ID in your AssetLoader constructor',
      );
    }

    const countryCode = getLocalization(store.getState());
    const id = assetBundleId;
    assetBundleId += 1;
    this.logDevMessage(
      `start loading an AWS asset map ${this.getNumberOfItems(AssetMap)}`,
    );
    this.progressCounter[id] = 0;

    const startTime = performance.now();
    const reportingDetails = {
      details: [],
      startTime,
    };

    Object.keys(AssetMap).forEach((categoryKey) => {
      Object.keys(AssetMap[categoryKey]).forEach((key) => {
        if (hasError) {
          return;
        }
        const assetObj = AssetMap[categoryKey][key];
        const { file, path, captions = {} } = assetObj;
        if (path === undefined) {
          // validating valid asset object
          console.warn(
            `asset is missing path field: ${AssetMap[categoryKey][key]}`,
          );
        }
        if (file === undefined) {
          // validating valid asset object
          console.warn(
            `asset is missing file field: ${AssetMap[categoryKey][key]}`,
          );
        }
        if (this.isAssetInCache(file, countryCode)) {
          // file has already been downloaded and is in your cache
          this.updateProgress(
            id,
            AssetMap,
            ProgressCallback,
            CompleteCallback,
            reportingDetails,
          );
        } else {
          const fetcher = this.getFileFetcher(assetObj);

          if (!fetcher) {
            const errorMessage = `File extension of "${file}" file is unknown for AWS loader!`;
            console.warn(errorMessage);
            reportingDetails.details.push(
              this.buildFetchStatusObject('error', file, path),
            );
            lib.throwGlobalError(errorMessage);
            return;
          }

          fetcher({ ...assetObj, countryCode })
            .then((asset) => {
              reportingDetails.details.push(
                this.buildFetchStatusObject('success', file, path),
              );

              if (this.getFileExtension(assetObj.file) !== 'svg') {
                this.storeInCache(file, countryCode, asset, captions);
              }

              this.updateProgress(
                id,
                AssetMap,
                ProgressCallback,
                CompleteCallback,
                reportingDetails,
              );
            })
            .catch((error) => {
              const errorMessage = `${error.message} | ${error.fileName}`;
              console.warn(errorMessage);
              reportingDetails.details.push(
                this.buildFetchStatusObject('error', file, path),
              );
              if (hasError === false) {
                hasError = true;
                this.updateProgress(
                  id,
                  AssetMap,
                  ProgressCallback,
                  CompleteCallback,
                  reportingDetails,
                );
                lib.throwGlobalError(errorMessage, error.name);
              }
            });
        }
      });
    });
  };

  /**
   * Builds the detail object for a specific asset used for reporting on each asset status.
   * @param status whether the asset fetch was a "success" or "error"
   * @param filename the filename of the asset to download
   * @param path the aws path of the asset being downloaded
   * @returns {{status: "success" | "error", filename: "filename", path: "path/to/file/directory/"}}
   */
  buildFetchStatusObject = (status, filename, path) => ({
    status,
    filename,
    path,
  });

  /**
   * Start process of a single asset load. handles catching if the asset fails to load
   * with the selected locale and will try to the load again with the default locale.
   * if the asset fails to load with the default locale then throw a warning and increment the
   * progress counter so the app doesn't freeze on the loading screen.
   * @param id unique id for this asset load
   * @param AssetMap Object containing assets to load. expecting this format:
   map{
      category1:{
        asset1: 'asset1',
        asset2: 'asset2',
        asset3: 'asset3',
      },
      category2:{
        asset1: 'asset1',
        asset2: 'asset2',
        asset3: 'asset3',
      },
    };
   Look at showOff > assets > svgComponents > SvgMap.js for example
   Look at showOff > assets > audio > AudioMap.js for example
   * @param filename name of the file to be loaded if SVG, .js extension should not be included. all other file types should include file extensions (.mp3)
   * @param countryCode country code to be loaded ('en','es')
   * @param CompleteCallback function to call when all the assets in passed map have been loaded
   * @param ProgressCallback function to call after each item in the map as been loaded to report back progress
   * @param reportingDetails object that logs successful and unsuccessful asset downloads for reporting.
   * @param attempts int to track the number of attempts this asset tried to load.
   */
  loadAsset = async (
    id,
    AssetMap,
    filename,
    countryCode,
    CompleteCallback,
    ProgressCallback,
    reportingDetails,
    attempts = 0,
  ) => {
    if (hasError) {
      return;
    }
    const status = await this.loadThroughLoaders(filename, countryCode).catch(
      (error) => {
        // TODO only 'en' country code will be retried multiple times. this means if an es asset fails to download because of a poor internet connection. it will retry it for en and may load the en version instead of the es version...
        if (countryCode !== 'en') {
          this.loadAsset(
            id,
            AssetMap,
            filename,
            'en',
            CompleteCallback,
            ProgressCallback,
            reportingDetails,
            0,
          );
        } else if (attempts < 3) {
          setTimeout(() => {
            this
              .logDevMessage(`$${filename}_${countryCode} failed to load | ${error}
                : trying again in ${lib.ASSET_RETRY_TIME} ms`);
            this.loadAsset(
              id,
              AssetMap,
              filename,
              countryCode,
              CompleteCallback,
              ProgressCallback,
              reportingDetails,
              attempts + 1,
            );
          }, lib.ASSET_RETRY_TIME);
        } else {
          reportingDetails.details.push(
            this.buildFetchStatusObject(
              'error',
              `${filename}_${countryCode}`,
              'webpack',
            ),
          );
          const errorMessage = `${filename}_${countryCode} failed to load | ${error}`;
          console.warn(errorMessage);
          if (hasError === false) {
            hasError = true;
            lib.throwGlobalError(errorMessage);
          }
        }
      },
    );

    if (status) {
      this.logDevMessage(
        `loadAsset ${filename}_${countryCode} status: Loaded Asset!`,
      );
      reportingDetails.details.push(
        this.buildFetchStatusObject(
          'success',
          `${filename}_${countryCode}`,
          'webpack',
        ),
      );
      this.updateProgress(
        id,
        AssetMap,
        ProgressCallback,
        CompleteCallback,
        reportingDetails,
      );
    } else {
      this.logDevMessage(
        `loadAsset ${filename}_${countryCode} status: 'FAILED to Load asset'`,
      );
    }
  };

  isAssetInCache = (filename, countryCode) => !!this.assetCache[`${filename}_${countryCode}`];

  /**
   * Loops through the loaders specified in the extended game specific version of this class using recursion
   * if it finds the asset then it loads it and stores it in the cache. if it doesn't it will try the
   * next loader in the list.
   * for loop would not properly await for loader. recursive function is safer.
   * @param filename name of the file to be loaded if SVG, .js extension should not be included. all other file types should include file extensions (.mp3)
   * @param countryCode countryCode to load
   * @param index of loader to try to load from. defaults to 1, used for recursive loop
   *
   */
  loadThroughLoaders = async (filename, countryCode, index = 0) => {
    // if asset is already cached, use that one.
    if (this.isAssetInCache(filename, countryCode)) {
      const cachedAsset = this.assetCache[`${filename}_${countryCode}`];
      return Promise.resolve(cachedAsset);
    }
    // ... else try the loaders.
    const asset = await this.loaders[index](filename, countryCode).catch(
      (error) => {
        this.logDevMessage(
          `${filename} failed to load with ${countryCode}` +
            ` and loader: ${
              this.loaders[index].name || this.loaders[index].file
            }. trying another loader`,
        );

        if (index === this.loaders.length - 1) {
          this.logDevMessage(`${filename} last loader couldn't find: ${error}`);
          throw error;
        }
        return this.loadThroughLoaders(filename, countryCode, index + 1);
      },
    );
    this.logDevMessage(`${filename} loaded successfully`);
    // TODO if object is in a later loader it may run through storeInCache multiple times.
    // Not a big deal but there is room for optimization here.\
    this.storeInCache(filename, countryCode, asset);
    return Promise.resolve(asset);
  };

  /**
   * A simple wrapper around a console log that will only show logs if this.showDebugLogs is true
   * @param message The message string you would like to print to the console.
   */
  logDevMessage = (message) => {
    if (this.showDebugLogs) {
      console.log(message);
    }
  };

  /**
   * returns the total number of items in an asset map
   * @param AssetMap the asset map to count the items in
   * @returns {number} number of items in the asset map
   */
  getNumberOfItems = (AssetMap) => {
    let count = 0;
    Object.keys(AssetMap).forEach((categoryKey) => {
      count += Object.keys(AssetMap[categoryKey]).length;
    });
    return count;
  };

  /**
   * Called from preLoadAssets to check if the last item has been loaded so the CompleteCallback can be triggered
   * @param loadedProgress currentProgress, number between 0 - 1
   * @param CompleteCallback the callback to call if progress is >= 1
   */
  callbackIfComplete = (loadedProgress, CompleteCallback) => {
    if (CompleteCallback) {
      if (loadedProgress >= 1) {
        CompleteCallback();
      }
    }
  };

  /**
   * Prints out your current asset auditStorage object to the console, showing you every asset you have loaded
   * and how many times you retrieved each asset with getLoadedAsset. Useful for finding assets that you may be
   * loading but never using.
   *
   * Must enable this.auditAssets at the top of the file for this function to work.
   * Must enable this.auditStorage and play through sections of your game, then call printAssetAudit.
   */
  printAssetAudit = () => {
    if (this.auditAssets) {
      console.log(this.auditStorage);
    } else {
      console.log(
        'You must enable this.auditAssets at the top of AssetLoader.js ' +
          'in order to print out your Asset Audit storage',
      );
    }
  };
}
const defaultErrorMessage =
  'Asset Loader failed to download your asset; showing global error screen';
/**
 * Dispatch action to show the global Alyx error page with "Something went wrong."
 */
export function throwGlobalError(
  errorMessage = defaultErrorMessage,
  errorName = 'Error',
) {
  console.warn(errorMessage);
  store.dispatch(
    throwCustomAquaError({
      titleText: ERROR_STRINGS.uhOh,
      secondaryText: ERROR_STRINGS.theresBeenAnError,
      showGamesButton: false,
      showHomeButton: true,
      forceLogout: false,
      errorMessage,
      errorName,
      category: 'Resource: Asset Loader',
      metricId: 'resource',
    }),
  );
}
